/**
 * "Made with iSpeedToClose" watermark — gated by the Free tier.
 *
 * Two variants share the same brand attribution but differ in form factor:
 *  - <WatermarkPill> for high-density share PNGs (Share Arc 1080×1080,
 *    Yard Sign 1080×1920) — sits in a corner, never blocks the deal.
 *  - <WatermarkBanner> for public web pages (/r/{token}, /wins/{token}) —
 *    a tasteful row above the footer with a clickable upgrade nudge.
 *
 * The pill is rendered inline so html2canvas captures it on PNG export.
 * Visiting the link tags the source as `?utm_source=watermark` so we can
 * measure how much organic traffic these create over time.
 */
import { Link } from "react-router-dom";
import AssignFlowLogo from "@/components/AssignFlowLogo";

const UPGRADE_URL = "/?utm_source=watermark&utm_medium=share";

/** Inline pill — embedded in Share Arc / Yard Sign PNGs. */
export function WatermarkPill({ inverted = false, style }) {
  // Inverted = dark-background card (Share Arc accent footer, Yard Sign footer).
  // Default = light card content area.
  const bg = inverted ? "rgba(255,255,255,0.14)" : "rgba(11,19,43,0.92)";
  const fg = inverted ? "#ffffff" : "#ffffff";
  const accent = inverted ? "#ffffff" : "#10B981";
  return (
    <div
      data-testid="watermark-pill"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: bg,
        color: fg,
        padding: "8px 12px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "-0.005em",
        whiteSpace: "nowrap",
        boxShadow: inverted ? "none" : "0 4px 10px rgba(11,19,43,0.18)",
        ...(style || {}),
      }}
    >
      <span
        style={{
          width: 20, height: 20, borderRadius: 5,
          background: inverted ? "rgba(255,255,255,0.18)" : "#0B132B",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <AssignFlowLogo size={14} accent={accent} />
      </span>
      Made with iSpeedToClose
    </div>
  );
}

/** Bottom banner — rendered on /r/{token} + /wins/{token}. */
export function WatermarkBanner() {
  return (
    <div
      data-testid="watermark-banner"
      className="border-t border-[var(--line)] bg-[var(--bg-2)]"
    >
      <div className="max-w-6xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center">
            <AssignFlowLogo size={20} />
          </span>
          <div className="text-sm leading-tight">
            <div className="font-display font-bold text-[var(--ink)]">
              This page was made with iSpeedToClose.
            </div>
            <div className="text-[var(--ink-3)] text-xs mt-0.5">
              Speed is the deal.
            </div>
          </div>
        </div>
        <Link
          to={UPGRADE_URL}
          className="btn-outline text-sm"
          data-testid="watermark-cta"
        >
          Try it free →
        </Link>
      </div>
    </div>
  );
}
